// @Version: 1.0.5
import { Component, EventEmitter, Output, forwardRef, Input, OnDestroy, OnChanges, signal, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS,  AbstractControl, ValidationErrors, ControlValueAccessor, Validator, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { ErrorMessagesService } from '../../../services/errorMessages/error-messages.service';
import { ValidatorsService } from '../../../services/validators/validators.service';
import { debounceTime } from 'rxjs/operators';
import { CustomValidators } from './custom-number-validators';
import { IErrorCodes } from '../../../services/errorMessages/error-messages.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FC_NUMBER, PATTERN, SEARCH } from './number.model';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { IFieldItem, VALIDATORS } from '../field-item.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-number',
   templateUrl: './number.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemNumberComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemNumberComponent),
         multi: true
      },
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemNumberComponent implements ControlValueAccessor, Validator, OnDestroy, OnChanges {
   
   public VALIDATORS = VALIDATORS;
   public FC_NUMBER = FC_NUMBER; 
   public done$$ = signal(false);
   
   public fgNumber: FormGroup = this.fb.group({});

   public fieldItem_: IFieldItem = {} as IFieldItem;
   public hideRequireMaker: boolean = false;

   private errorCodes_: IErrorCodes = {} as IErrorCodes;
   private decimal_: number = 0; 
   private sub_?: Subscription;
   
   @Input() clearValueIfDisabled: boolean = true;

   @Input() 
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }   
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }


   @Input()
   set value (val: string) {  
      this.fgNumber.get(FC_NUMBER.NUMBER)?.setValue(val);
   }
   get value (): string {
      return this.fgNumber.get(FC_NUMBER.NUMBER)?.value ?? '';
   }   

   get error (): IErrorCodes {
      return this.errorCodes_;
   }

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();


   constructor (private vValidators: CustomValidators, private fb: FormBuilder, public sErrorMessages: ErrorMessagesService, public sValidators: ValidatorsService) { 
   }
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_5 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.transformDecimal(changes.fieldItem.currentValue.attribute_4 ?? "");
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }
   ngOnDestroy (): void {
     this.reset();
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgNumber.updateValueAndValidity({emitEvent: true, onlySelf: true});
      return this.fgNumber.get(FC_NUMBER.NUMBER) ? this.fgNumber.get(FC_NUMBER.NUMBER)!.valid ? null : this.fgNumber.get(FC_NUMBER.NUMBER)!.errors : null;
   }

   writeValue (value: string | number | undefined | null): void {
      this.fgNumber.get(FC_NUMBER.NUMBER)?.setValue(value ? value : undefined);
      if ((typeof value == 'string' && value.trim().length > 0) || typeof value == 'number') this.setDirtyTouched();
      this.onValidationChange();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }

   registerOnValidatorChange?(fn: () => void): void {
      this.onValidationChange = fn;
   }

   private onTouched () {}
   private onChange (_: string) {}

   onValidationChange: any = () => {}; 

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgNumber.get(FC_NUMBER.NUMBER)) isDisabled ? this.setDisabledControls() : this.fgNumber.get(FC_NUMBER.NUMBER)?.enable(); 
      this.onValidationChange();
   }

   private reset (): void {
      this.done$$.set(false);
      this.fgNumber = this.fb.group({});
      this.fieldItem_ = {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.decimal_ = 0; 
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      this.fgNumber =  this.fb.group({
         [FC_NUMBER.NUMBER]: this.fb.control({value: fieldItem.content, disabled: false}, {validators: this.sValidators.setValidators(fieldItem), updateOn: 'change'})
      });
      this.setValidators(fieldItem);
   
      this.sub_ =  this.fgNumber.get(FC_NUMBER.NUMBER)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(() => {
         if (!this.value && this.fgNumber.get(FC_NUMBER.NUMBER)?.pristine) return;
         this.onChange(this.value);
         this.onTouched();
         this.onValidationChange();
      })

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker); 

      return true;
   }

   private setValidators (fieldItem: IFieldItem): void {
      this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators(this.decimal_ > 0 ? Validators.pattern(`^-?[0-9]*$|^-?[0-9]*\\,[0-9]{1,${this.decimal_}}$|^-?[0-9]{1,3}(\\.[0-9]{3})*\\,[0-9]{1,${this.decimal_}}$|^-?[0-9]{1,3}(\\.[0-9]{3})*$`) : Validators.pattern(PATTERN.NUMBER));

      switch (fieldItem.valid) {
         case VALIDATORS.positiv: this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators([this.vValidators.isPosValid(this.fgNumber)]); break;
         case VALIDATORS.negativ: this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators([this.vValidators.isNegValid(this.fgNumber)]); break;
         case VALIDATORS.range: this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators([this.vValidators.isRangeValid(this.fgNumber, +(fieldItem.attribute_2!.replace(",", ".")) , +(fieldItem.attribute_3!.replace(",", "."))) ]); break;
         case VALIDATORS.maxnumber: this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators([this.vValidators.isMaxValid(this.fgNumber, +(fieldItem.attribute_3!.replace(",", ".")))]); break;
         case VALIDATORS.minnumber: this.fgNumber.get(FC_NUMBER.NUMBER)?.addValidators([this.vValidators.isMinValid(this.fgNumber,+(fieldItem.attribute_2!.replace(",", ".")))]); break;
      }
   }
  
   private setDisabledControls (): void {
      this.fgNumber.get(FC_NUMBER.NUMBER)?.disable();
      if (this.clearValueIfDisabled) this.fgNumber.get(FC_NUMBER.NUMBER)?.setValue(""); 
      this.fgNumber.get(FC_NUMBER.NUMBER)?.markAsTouched(); 
   }

   private transformDecimal (val: string): void {
      const find = val.substring(val.search(SEARCH.DECIMAL) + SEARCH.DECIMAL.length); 
      this.decimal_ = !isNaN(+find) ? +find : 0;

      if (this.errorCodes_.errorCodeDecimal) {
         let errorDecimal = ""; 
         for(let i = 0; i < this.decimal_; i++) errorDecimal += "#";
         this.errorCodes_.errorCodeDecimal = this.errorCodes_.errorCodeDecimal.replace(SEARCH.REPLACE, errorDecimal);
      }
   }

   private setDirtyTouched (): void {
      this.fgNumber.get(FC_NUMBER.NUMBER)?.markAsDirty(); 
      this.fgNumber.get(FC_NUMBER.NUMBER)?.markAsTouched();
   }
}